import { useState, useEffect, useCallback } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  ArrowLeft, Search, Database, TrendingUp, Target, BookOpen,
  BarChart3, Eye, EyeOff, Filter,
  Brain, AlertCircle, Play
} from 'lucide-react';
import { useAuthStore } from '../lib/auth';
import api from '../lib/api';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png";

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
      <CardContent className="p-4 text-center">
        <Icon className={`w-7 h-7 mx-auto mb-2 ${color}`} />
        <p className="text-2xl sm:text-3xl font-bold text-white">{value}</p>
        <p className="text-gray-400 text-xs sm:text-sm">{label}</p>
      </CardContent>
    </Card>
  );
}

function ClueCard({ clue, index }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div
      className="bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--border))] rounded-lg p-4 overflow-hidden"
      data-testid={`clue-result-${index}`}
    >
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge className="bg-[hsl(var(--brand-gold-500))]/20 text-[hsl(var(--brand-gold-500))] text-xs truncate max-w-[60vw] sm:max-w-none">
          {clue.category}
        </Badge>
        {clue.value && (
          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
            ${clue.value}
          </Badge>
        )}
        {clue.difficulty && (
          <Badge className="bg-purple-500/20 text-purple-400 text-xs">
            {clue.difficulty}
          </Badge>
        )}
      </div>
      <p className="text-gray-200 text-sm mb-3 break-words">{clue.question}</p>
      <button
        onClick={() => setRevealed(!revealed)}
        className="flex items-center gap-2 text-xs text-gray-400 hover:text-white transition-colors"
        data-testid={`reveal-answer-${index}`}
      >
        {revealed ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        {revealed ? clue.answer : 'Reveal Answer'}
      </button>
    </div>
  );
}

export default function ClueExplorer() {
  useSEO('Clue Explorer | JepPrep', 'Search and explore the JepPrep clue database. Discover patterns, top answers, and find categories to practice.');
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const [activeSection, setActiveSection] = useState('insights');
  const [stats, setStats] = useState(null);
  const [topAnswers, setTopAnswers] = useState([]);
  const [topCategories, setTopCategories] = useState([]);
  const [hardestCategories, setHardestCategories] = useState([]);
  const [unpracticed, setUnpracticed] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search state
  const [keyword, setKeyword] = useState('');
  const [searchCategory, setSearchCategory] = useState('');
  const [searchDifficulty, setSearchDifficulty] = useState('');
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchPage, setSearchPage] = useState(1);
  const [searching, setSearching] = useState(false);
  const [categorySuggestions, setCategorySuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    fetchInsights();
  }, []);

  const fetchInsights = async () => {
    try {
      setLoading(true);
      const [statsRes, answersRes, categoriesRes, hardestRes, unpracticedRes] = await Promise.all([
        api.get('/api/clue-explorer/stats'),
        api.get('/api/clue-explorer/top-answers?limit=10'),
        api.get('/api/clue-explorer/top-categories?limit=10'),
        api.get('/api/clue-explorer/hardest-categories'),
        api.get('/api/clue-explorer/unpracticed-categories')
      ]);
      setStats(statsRes.data);
      setTopAnswers(answersRes.data);
      setTopCategories(categoriesRes.data);
      setHardestCategories(hardestRes.data);
      setUnpracticed(unpracticedRes.data);
    } catch (error) {
      console.error('Error fetching clue explorer data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = useCallback(async (page = 1) => {
    try {
      setSearching(true);
      setSearchPage(page);
      const params = new URLSearchParams();
      if (keyword) params.set('keyword', keyword);
      if (searchCategory) params.set('category', searchCategory);
      if (searchDifficulty) params.set('difficulty', searchDifficulty);
      if (minValue) params.set('min_value', minValue);
      if (maxValue) params.set('max_value', maxValue);
      params.set('page', page);
      params.set('limit', '20');

      const res = await api.get(`/api/clue-explorer/search?${params.toString()}`);
      setSearchResults(res.data);
      setActiveSection('search');
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  }, [keyword, searchCategory, searchDifficulty, minValue, maxValue]);

  const fetchCategorySuggestions = async (q) => {
    if (q.length < 2) {
      setCategorySuggestions([]);
      return;
    }
    try {
      const res = await api.get(`/api/clue-explorer/category-suggestions?q=${encodeURIComponent(q)}`);
      setCategorySuggestions(res.data);
      setShowSuggestions(true);
    } catch (e) {
      // ignore
    }
  };

  const handlePracticeCategory = (categoryName) => {
    navigate(`/sprint?category=${encodeURIComponent(categoryName)}&num=15`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-white text-xl">Loading Clue Explorer...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] overflow-x-hidden">
      {/* Header */}
      <header className="bg-[hsl(var(--secondary))] border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/askalex">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10 shrink-0">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="Jeopardy practice" className="h-12 shrink-0" />
              <h1 className="text-lg sm:text-xl font-display text-white hidden sm:block">Clue Explorer</h1>
            </div>
            <Link to="/askalex">
              <Button className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-sm shrink-0">
                <Brain className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">AskAlex</span>
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <StatCard
            icon={Database}
            label="Total Clues"
            value={stats?.total_clues?.toLocaleString() || '0'}
            color="text-[hsl(var(--brand-gold-500))]"
          />
          <StatCard
            icon={BarChart3}
            label="Categories"
            value={stats?.unique_categories?.toLocaleString() || '0'}
            color="text-cyan-400"
          />
          <StatCard
            icon={Target}
            label="Difficulties"
            value={Object.keys(stats?.by_difficulty || {}).length}
            color="text-purple-400"
          />
        </div>

        {/* Section Toggle */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2" data-testid="clue-explorer-tabs">
          {[
            { id: 'insights', label: 'Insights', icon: TrendingUp },
            { id: 'search', label: 'Search', icon: Search },
            { id: 'practice', label: 'Practice Gaps', icon: Target }
          ].map(({ id, label, icon: Icon }) => (
            <Button
              key={id}
              variant={activeSection === id ? 'default' : 'outline'}
              className={activeSection === id
                ? 'bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold shrink-0'
                : 'border-[hsl(var(--border))] text-gray-400 hover:text-white shrink-0'
              }
              onClick={() => setActiveSection(id)}
              data-testid={`tab-${id}`}
            >
              <Icon className="w-4 h-4 mr-2" />
              {label}
            </Button>
          ))}
        </div>

        {/* INSIGHTS SECTION */}
        {activeSection === 'insights' && (
          <div className="space-y-6" data-testid="insights-section">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Top 10 Answers - List */}
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardHeader className="pb-2 px-4 pt-4">
                  <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                    <TrendingUp className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                    Top 10 Answers
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-2">
                    {topAnswers.map((item, idx) => {
                      const maxCount = topAnswers[0]?.count || 1;
                      return (
                        <div key={item.answer} className="flex items-center gap-3">
                          <span className={`text-xs font-bold w-5 text-right shrink-0 ${idx < 3 ? 'text-[hsl(var(--brand-gold-500))]' : 'text-gray-500'}`}>
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-white text-sm truncate">{item.answer}</span>
                              <span className="text-gray-400 text-xs shrink-0 ml-2">{item.count.toLocaleString()}</span>
                            </div>
                            <div className="w-full bg-[hsl(var(--secondary))] rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${idx < 3 ? 'bg-[hsl(var(--brand-gold-500))]' : 'bg-[hsl(222,47%,45%)]'}`}
                                style={{ width: `${(item.count / maxCount) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Most Popular Categories - List */}
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardHeader className="pb-2 px-4 pt-4">
                  <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                    <BarChart3 className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                    Most Popular Categories
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-2">
                    {topCategories.map((item, idx) => {
                      const maxCount = topCategories[0]?.count || 1;
                      return (
                        <div key={item.category} className="flex items-center gap-3">
                          <span className={`text-xs font-bold w-5 text-right shrink-0 ${idx < 3 ? 'text-[hsl(var(--brand-gold-500))]' : 'text-gray-500'}`}>
                            {idx + 1}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-white text-sm truncate">{item.category}</span>
                              <span className="text-gray-400 text-xs shrink-0 ml-2">{item.count.toLocaleString()}</span>
                            </div>
                            <div className="w-full bg-[hsl(var(--secondary))] rounded-full h-1.5">
                              <div
                                className={`h-1.5 rounded-full ${idx < 3 ? 'bg-[hsl(var(--brand-gold-500))]' : 'bg-[hsl(222,47%,45%)]'}`}
                                style={{ width: `${(item.count / maxCount) * 100}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Difficulty Breakdown */}
            {stats?.by_difficulty && (
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardHeader className="pb-2 px-4 pt-4">
                  <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                    <Target className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                    Clues by Difficulty
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {Object.entries(stats.by_difficulty).map(([diff, count]) => (
                      <div key={diff} className="bg-[hsl(var(--secondary))] rounded-lg p-4 flex sm:flex-col items-center sm:text-center gap-3 sm:gap-0">
                        <p className="text-xl sm:text-2xl font-bold text-white">{count.toLocaleString()}</p>
                        <p className="text-gray-400 text-sm capitalize">{diff}</p>
                        <p className="text-gray-500 text-xs ml-auto sm:ml-0">
                          {((count / stats.total_clues) * 100).toFixed(0)}%
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Hardest Categories for User */}
            {hardestCategories.length > 0 && (
              <Card className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))]">
                <CardHeader className="pb-2 px-4 pt-4">
                  <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                    <AlertCircle className="w-4 h-4 text-rose-400" />
                    Your Hardest Categories
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-2">
                    {hardestCategories.slice(0, 10).map((cat, idx) => (
                      <div key={cat.category} className="bg-[hsl(var(--secondary))] rounded-lg px-4 py-3">
                        <div className="flex items-center justify-between mb-2 sm:mb-0">
                          <div className="flex items-center gap-3 min-w-0">
                            <span className="text-gray-500 text-sm w-6 shrink-0">{idx + 1}.</span>
                            <span className="text-white text-sm truncate">{cat.category}</span>
                          </div>
                          <span className={`text-sm font-medium shrink-0 sm:hidden ${
                            cat.accuracy < 40 ? 'text-rose-400' : cat.accuracy < 60 ? 'text-amber-400' : 'text-emerald-400'
                          }`}>
                            {cat.accuracy}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between pl-9">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500 text-xs">({cat.correct}/{cat.total})</span>
                            <span className={`text-sm font-medium hidden sm:inline ${
                              cat.accuracy < 40 ? 'text-rose-400' : cat.accuracy < 60 ? 'text-amber-400' : 'text-emerald-400'
                            }`}>
                              {cat.accuracy}%
                            </span>
                          </div>
                          <Button
                            size="sm"
                            className="bg-[hsl(var(--brand-gold-500))]/20 text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-gold-500))] hover:text-[hsl(var(--brand-blue-900))] text-xs h-7 px-2"
                            onClick={() => handlePracticeCategory(cat.category)}
                            data-testid={`practice-hard-${idx}`}
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Practice
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* SEARCH SECTION */}
        {activeSection === 'search' && (
          <div className="space-y-6" data-testid="search-section">
            {/* Search Filters */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-5 h-5 text-[hsl(var(--brand-gold-500))]" />
                  <h3 className="text-white font-semibold">Search Filters</h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                  {/* Keyword */}
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Keyword</label>
                    <Input
                      placeholder="Search clues or answers..."
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                      className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] text-white text-sm"
                      data-testid="search-keyword"
                    />
                  </div>

                  {/* Category with autocomplete */}
                  <div className="relative">
                    <label className="text-gray-400 text-xs mb-1 block">Category</label>
                    <Input
                      placeholder="e.g. SCIENCE"
                      value={searchCategory}
                      onChange={(e) => {
                        setSearchCategory(e.target.value);
                        fetchCategorySuggestions(e.target.value);
                      }}
                      onFocus={() => searchCategory.length >= 2 && setShowSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                      className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] text-white text-sm"
                      data-testid="search-category"
                    />
                    {showSuggestions && categorySuggestions.length > 0 && (
                      <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--border))] rounded-lg shadow-lg max-h-48 overflow-y-auto">
                        {categorySuggestions.map((s) => (
                          <button
                            key={s.category}
                            className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-[hsl(var(--brand-gold-500))]/20 hover:text-white flex justify-between"
                            onMouseDown={() => {
                              setSearchCategory(s.category);
                              setShowSuggestions(false);
                            }}
                          >
                            <span>{s.category}</span>
                            <span className="text-gray-500 text-xs">{s.count.toLocaleString()}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Difficulty */}
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Difficulty</label>
                    <select
                      value={searchDifficulty}
                      onChange={(e) => setSearchDifficulty(e.target.value)}
                      className="w-full h-9 rounded-md bg-[hsl(var(--brand-blue-800))] border border-[hsl(var(--border))] text-white text-sm px-3"
                      data-testid="search-difficulty"
                    >
                      <option value="">All</option>
                      <option value="college">College</option>
                      <option value="adult">Adult</option>
                      <option value="masters">Masters</option>
                    </select>
                  </div>

                  {/* Value Range */}
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Min Value ($)</label>
                    <Input
                      type="number"
                      placeholder="e.g. 200"
                      value={minValue}
                      onChange={(e) => setMinValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                      className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] text-white text-sm"
                      data-testid="search-min-value"
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Max Value ($)</label>
                    <Input
                      type="number"
                      placeholder="e.g. 1000"
                      value={maxValue}
                      onChange={(e) => setMaxValue(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch(1)}
                      className="bg-[hsl(var(--brand-blue-800))] border-[hsl(var(--border))] text-white text-sm"
                      data-testid="search-max-value"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => handleSearch(1)}
                    disabled={searching}
                    className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] font-semibold hover:bg-[hsl(var(--brand-blue-800))] hover:text-white"
                    data-testid="search-submit"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                  <Button
                    variant="outline"
                    className="border-[hsl(var(--border))] text-gray-400 hover:text-white"
                    onClick={() => {
                      setKeyword('');
                      setSearchCategory('');
                      setSearchDifficulty('');
                      setMinValue('');
                      setMaxValue('');
                      setSearchResults(null);
                    }}
                    data-testid="search-clear"
                  >
                    Clear
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Search Results */}
            {searchResults && (
              <div data-testid="search-results">
                <div className="flex items-center justify-between mb-4">
                  <p className="text-gray-400 text-sm">
                    {searchResults.total.toLocaleString()} results found
                    {searchResults.pages > 1 && ` (page ${searchResults.page} of ${searchResults.pages})`}
                  </p>
                </div>

                <div className="space-y-3">
                  {searchResults.results.map((clue, idx) => (
                    <ClueCard key={clue.id || clue.clue_id || `clue-${idx}`} clue={clue} index={idx} />
                  ))}
                </div>

                {searchResults.results.length === 0 && (
                  <div className="text-center py-12">
                    <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-400">No clues match your filters</p>
                  </div>
                )}

                {/* Pagination */}
                {searchResults.pages > 1 && (
                  <div className="flex items-center justify-center gap-3 mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={searchResults.page <= 1}
                      onClick={() => handleSearch(searchResults.page - 1)}
                      className="border-[hsl(var(--border))] text-gray-400"
                      data-testid="search-prev"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Prev
                    </Button>
                    <span className="text-gray-400 text-sm">
                      {searchResults.page} / {searchResults.pages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={searchResults.page >= searchResults.pages}
                      onClick={() => handleSearch(searchResults.page + 1)}
                      className="border-[hsl(var(--border))] text-gray-400"
                      data-testid="search-next"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                )}
              </div>
            )}

            {!searchResults && (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">Use the filters above to search through {stats?.total_clues?.toLocaleString()} clues</p>
              </div>
            )}
          </div>
        )}

        {/* PRACTICE GAPS SECTION */}
        {activeSection === 'practice' && (
          <div className="space-y-6" data-testid="practice-section">
            {/* Unpracticed Categories */}
            <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
              <CardHeader className="pb-2">
                <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                  <BookOpen className="w-4 h-4 text-[hsl(var(--brand-gold-500))]" />
                  Categories You Haven't Practiced
                </CardTitle>
                <p className="text-gray-400 text-xs mt-1">These popular categories are waiting for you</p>
              </CardHeader>
              <CardContent>
                {unpracticed.length > 0 ? (
                  <div className="grid sm:grid-cols-2 gap-3">
                    {unpracticed.map((cat, idx) => (
                      <div
                        key={cat.category}
                        className="flex items-center justify-between bg-[hsl(var(--brand-blue-800))] rounded-lg px-4 py-3 border border-[hsl(var(--border))]"
                      >
                        <div>
                          <p className="text-white text-sm font-medium">{cat.category}</p>
                          <p className="text-gray-500 text-xs">{cat.clue_count.toLocaleString()} clues available</p>
                        </div>
                        <Button
                          size="sm"
                          className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white text-xs h-8 px-3"
                          onClick={() => handlePracticeCategory(cat.category)}
                          data-testid={`practice-unpracticed-${idx}`}
                        >
                          <Play className="w-3 h-3 mr-1" />
                          Practice
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Target className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
                    <p className="text-emerald-400 font-medium">Impressive!</p>
                    <p className="text-gray-400 text-sm">You've practiced across all major categories</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Hardest Categories */}
            {hardestCategories.length > 0 && (
              <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-white flex items-center gap-2 text-sm sm:text-base">
                    <AlertCircle className="w-4 h-4 text-rose-400" />
                    Categories to Improve
                  </CardTitle>
                  <p className="text-gray-400 text-xs mt-1">Focus here to raise your overall accuracy</p>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {hardestCategories.slice(0, 10).map((cat, idx) => (
                      <div key={cat.category} className="flex items-center justify-between bg-[hsl(var(--brand-blue-800))] rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-gray-500 text-sm w-6 shrink-0">{idx + 1}.</span>
                          <div className="min-w-0">
                            <p className="text-white text-sm truncate">{cat.category}</p>
                            <p className="text-gray-500 text-xs">{cat.correct}/{cat.total} correct</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="w-16 bg-[hsl(var(--secondary))] rounded-full h-2">
                            <div
                              className={`h-2 rounded-full ${
                                cat.accuracy < 40 ? 'bg-rose-400' : cat.accuracy < 60 ? 'bg-amber-400' : 'bg-emerald-400'
                              }`}
                              style={{ width: `${cat.accuracy}%` }}
                            />
                          </div>
                          <span className={`text-sm font-medium w-12 text-right ${
                            cat.accuracy < 40 ? 'text-rose-400' : cat.accuracy < 60 ? 'text-amber-400' : 'text-emerald-400'
                          }`}>
                            {cat.accuracy}%
                          </span>
                          <Button
                            size="sm"
                            className="bg-[hsl(var(--brand-gold-500))]/20 text-[hsl(var(--brand-gold-500))] hover:bg-[hsl(var(--brand-gold-500))] hover:text-[hsl(var(--brand-blue-900))] text-xs h-7 px-2"
                            onClick={() => handlePracticeCategory(cat.category)}
                            data-testid={`practice-improve-${idx}`}
                          >
                            <Play className="w-3 h-3 mr-1" />
                            Practice
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
